import { DOCS_PAGE_TRANSLATIONS_AR } from './content.ar'
import { DOCS_PAGE_TRANSLATIONS_JA } from './content.ja'
import { DOCS_PAGE_TRANSLATIONS_ES } from './content.es'
import { DOCS_PAGE_TRANSLATIONS_KO } from './content.ko'
import type { DocsLocale, DocsPage, DocsPageTranslation, DocsTranslationStatus } from './types'
import { MCP_CLIENTS } from '@/lib/mcpDocsClients'

type DocsSource = Pick<DocsPage, 'title' | 'description' | 'overview'>

function hashString(input: string): string {
  let h1 = 0xdeadbeef
  let h2 = 0x41c6ce57

  for (let i = 0; i < input.length; i++) {
    const ch = input.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }

  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909)

  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36)
}

export function getDocsSourceHash(page: DocsSource): string {
  return `v1:${hashString(JSON.stringify({ title: page.title, description: page.description, overview: page.overview }))}`
}

export const DOCS_PAGES: DocsPage[] = [
  // ---------------------------------------------------------------------------
  // Getting started
  // ---------------------------------------------------------------------------
  {
    slug: 'getting-started/welcome',
    title: 'Welcome to Martini',
    section: 'getting-started',
    order: 0,
    description: 'Meet the canvas-to-cut workflow for developing shots with generative models.',
    overview: `Martini is a filmmaking workspace for developing shots with generative models. You can gather references, generate takes, compare directions, and build an edit without splitting the work across several tools.

Your project begins on an infinite canvas. Place shots and references wherever they help you think, then keep each new take beside the work that led to it. Nothing needs to fit into a folder before you are ready.

![](/docs-imgs/welcome1.mp4)

Press ||V|| to place a shot. Write a prompt, choose a model, and generate. If the take misses the mark, change the direction, swap a reference, or try another model. Keeping the attempts together makes it easier to see what is working.

![](/docs-imgs/welcome2.mp4)

When the shots are ready, drag your selects to the timeline, shape the sequence, and export it. Martini gives you one workflow across Veo, Kling, Sora, and other supported models, so the creative decision can come before the provider.`,
  },
  {
    slug: 'getting-started/first-shot',
    title: 'Your first shot',
    section: 'getting-started',
    order: 2,
    description: 'Generate an image, then turn it into video.',
    overview: `Every project starts with a blank canvas. Press ||V|| to place a shot, write a prompt, pick a model, and generate. That is the core workflow. The rest is iteration.

Martini supports image-to-video, video-to-video, and text-to-video generation. Choose the approach that fits the shot. Many filmmakers start with a generated or uploaded image, then use it as the start frame for video. The image anchors the composition; the model handles the motion.`,
    featureIds: ['placement-video'],
  },
  {
    slug: 'getting-started/iterating',
    title: 'Iterating on shots',
    section: 'getting-started',
    order: 3,
    description: 'Refine shots through rapid iteration.',
    overview: `The first generation is rarely the final one. Rewrite the prompt, swap the reference image, try a different model, or adjust the duration. Every take lands beside the original so you can compare them side by side.

Martini is built around a simple loop: generate, review, adjust, regenerate. The canvas gives you room to spread out options. Stacks keep related takes together when you are refining one shot.`,
  },
  {
    slug: 'getting-started/first-project',
    title: 'Your first project',
    section: 'getting-started',
    order: 1,
    description: 'Go from a blank canvas to a reviewable export.',
    overview: `This walkthrough covers the smallest complete Martini workflow. You will generate one shot, review the take, place it on the timeline, and export an MP4.

Create the shot

- Open a project and press ||V|| to place a video shot on the blank canvas.
- Write a prompt that names the subject, action, framing, and camera movement.
- Pick a model and check the olive estimate before you select Generate. Generation spends olives.
- Wait for the take to appear beside the shot on the canvas.

Review and refine

- Play the take with ||Space||. Check the composition, motion, and continuity you care about.
- If it misses, change one meaningful part of the prompt or reference, then generate another take.
- Keep promising takes beside each other or group them into a stack for comparison.

Cut and export

- Drag the selected take from the canvas to the timeline.
- Trim the shot by dragging either edge, then play the sequence from the timeline.
- Open Export and choose MP4. Use XML instead when you plan to finish in Premiere Pro or DaVinci Resolve.

You are done when the exported file plays with the same shot order and trim points you reviewed on the timeline.`,
  },
  {
    slug: 'getting-started/troubleshooting',
    title: 'Troubleshooting',
    section: 'getting-started',
    order: 4,
    description: 'Resolve common generation, playback, and export problems.',
    overview: `Generation does not start

- Check that the required prompt and reference fields are filled in.
- Review the olive estimate and your available balance.
- Try another model if the selected model is temporarily unavailable.

A take does not match the prompt

- Describe physical action, camera movement, framing, and light instead of adding more adjectives.
- Change one instruction at a time so you can tell what improved the result.
- Add a start frame or reference image when composition needs tighter control.

Playback or export looks wrong

- Play the shot on the canvas first, then play the full sequence from the timeline.
- Confirm the intended take is on the timeline and check each trim point.
- For XML exports, keep the XML file and media folder together before importing into your editor.

If the issue persists, include the project URL, the model name, and what you expected when you contact support. Do not share API keys or billing details.`,
  },

  // ---------------------------------------------------------------------------
  // Canvas
  // ---------------------------------------------------------------------------
  {
    slug: 'canvas/overview',
    title: 'Canvas overview',
    navTitle: 'Overview',
    section: 'canvas',
    order: 0,
    description: 'The infinite workspace where everything lives.',
    overview: `The canvas is an infinite 2D workspace. Your project lives here: shots, images, audio, sets, and text annotations. There are no pages or folders to navigate. You organize spatially, the way you would lay out selects on a light table or arrange references on a wall.

Every object on the canvas follows the same rules for selection, movement, and organization. Zoom out to see the whole project or zoom in to inspect a frame. Scroll to zoom, two-finger drag to pan, and press ||Shift 1|| to fit everything in view.`,
  },
  {
    slug: 'canvas/nodes',
    title: 'Canvas objects',
    section: 'canvas',
    order: 1,
    description: 'Choose the right canvas object for shots, references, notes, and reusable material.',
    overview: `A canvas object is a piece of your project that you can place, move, and organize. Use the object that matches the work: a video shot for moving images, an image for a still or reference, audio for sound, and text for a note.

Library collections and variables help you reuse creative direction. A collection keeps the references for a character, location, or prop together. Collections belong to your workspace; enable one in a project from the Library tab, then reference it with @ in any prompt there. A variable stores prompt text that you expect to use again. Sets create navigable 3D environments, while bins gather related canvas work without hiding it in a folder.

To add an object, press its shortcut and click the canvas: ||V|| for video, ||I|| for image, ||A|| for audio, ||T|| for text, ||B|| for a bin, ||Shift V|| for a variable, or ||Shift S|| for a set. You can also choose an object from the Add menu.`,
    featureIds: [
      'placement-video',
      'placement-image',
      'placement-text',
      'placement-bin',
      'placement-variable',
      'placement-set',
      'placement-audio',
    ],
  },
  {
    slug: 'canvas/selection',
    title: 'Selection',
    section: 'canvas',
    order: 2,
    description: 'Select one object or gather several for a shared canvas action.',
    overview: `Select an object when you want to inspect or change it. Click once to select it; the selection border confirms which object will receive the next action. The inspector shows the settings for the object you selected most recently.

To work with several objects, hold ||Shift|| while clicking or drag a marquee across empty canvas. You can then move, delete, rate, or label the selection together. Press ||⌘A|| to select everything currently on the canvas.

Drag an object to move it without changing its contents. If several objects are selected, dragging any one of them moves the whole selection and preserves their relative positions.

Press ||Escape|| or click an empty area to clear the selection. If you are about to delete or move a group, pause to check every visible selection border first.`,
    featureIds: ['selection-click', 'selection-select-all', 'selection-escape', 'drag-node'],
  },
  {
    slug: 'canvas/tablet-controls',
    title: 'Tablet controls',
    section: 'canvas',
    order: 3,
    description: 'Navigate and arrange the canvas with touch-friendly controls.',
    overview: `Tablet mode keeps the canvas visible while moving its main actions into larger touch targets. Martini enables it automatically on supported tablets, and you can switch between the touch and desktop layouts at any time.

Use tablet mode to review, organize, and place work away from a keyboard. A few precision workflows still require the desktop layout; those are listed at the end of this page.

Turn on tablet mode

- From the desktop layout, tap the outlined tablet button in the canvas toolbar. The Touch controls off tooltip confirms that tablet mode is available but disabled.

![The tablet button showing Touch controls off](/docs-imgs/tablet-controls-off.png)

- In tablet mode, the tablet button stays highlighted and the tooltip reads Touch controls on. Tap it again to return to desktop controls.

![The highlighted tablet button showing Touch controls on](/docs-imgs/tablet-controls-on.png)

Tablet layout

- The top bar keeps the project menu, Create and Edit modes, notifications, settings, project controls, account menu, and Share button available.
- The tablet toolbar sits in the lower-right corner. Add stays in the lower-left corner, and Show assets stays in the upper-left corner.

![The complete canvas in tablet mode](/docs-imgs/tablet-layout-overview.png)

Use the tablet toolbar

- Scissors: open or close the timeline.
- Search: find commands and tools.
- Overlapping squares: enter or exit multi-select mode.
- Tablet: turn tablet controls off or back on.
- Comment bubble: add or review comments.
- Zoom controls: zoom out, set an exact level, zoom in, or fit the project in view.
- Filter: show only matching canvas work.

Open the timeline

- Tap the scissors button to open or close the timeline without leaving tablet mode.

![The Open timeline button in the tablet toolbar](/docs-imgs/tablet-open-timeline.png)

Select multiple

- Tap the overlapping-squares button, then tap objects to add or remove them from the selection.
- Hold and drag any selected object to move the full selection together.

![The Select multiple button in the tablet toolbar](/docs-imgs/tablet-select-multiple.png)

Add to the canvas

- Tap the plus button in the lower-left corner to open the object toolbar.
- Choose Image, Video, Audio, Set, Text, Bin, or Variable, then tap the canvas to place it.

![The vertical Add to canvas object toolbar](/docs-imgs/tablet-object-toolbar.png)

Show and hide assets

- Tap Show assets in the upper-left corner to open the asset panel. Tap the same control again to hide it.

![The Show assets button in tablet mode](/docs-imgs/tablet-show-assets.png)

Return to desktop controls

- Tap the highlighted tablet button to turn touch controls off. The canvas returns to the desktop layout with its compact toolbar in the upper-right corner.

![The canvas after returning to desktop controls](/docs-imgs/tablet-desktop-layout.png)

Select and move work

- Tap an object to select it. Tap empty canvas to clear the selection.
- Hold an object until the four-arrow move indicator appears, then drag. Hold and drag any selected object to move the full selection.
- Drag empty canvas with one finger to pan. Pinch with two fingers to zoom or pan while zooming.

Desktop-only workflows

- Advanced timeline editing, dragging work from the canvas to the timeline, marquee selection, and keyboard shortcuts still require the desktop layout.
- Martini does not currently provide specialized Apple Pencil controls.`,
  },
  {
    slug: 'canvas/organizing',
    title: 'Organizing',
    section: 'canvas',
    order: 5,
    description: 'Color labels, ratings, bins, and filters.',
    overview: `Press ||1|| through ||5|| to apply a star rating. Press ||6|| through ||0|| to apply a color label. ||F|| marks a favorite, and ||R|| marks a reject. These are the triage tools editors use when pulling selects from dailies: rate promising takes, flag favorites, reject weak ones, and sort through what remains.

Press ||B|| to create a bin, then drag shots into it to group them on the canvas. Bins are containers, not folders; everything stays visible and accessible. Press ||Shift F|| to open filters for favorites, rejects, or a specific color label. Filters only change what you see.`,
    featureIds: ['rating-favorite', 'rating-reject', 'placement-bin'],
  },
  {
    slug: 'canvas/uploading',
    title: 'Uploading media',
    section: 'canvas',
    order: 4,
    description: 'Bring images, video, and audio onto the canvas.',
    overview: `Drag one or more files from Finder onto an open area of the canvas. Martini uploads each file and creates the matching canvas object: images become image nodes, videos become shots, and audio becomes an audio node.

After the upload

- Wait for the upload to finish before closing the project.
- Rename useful media so it is easy to find later.
- Use an uploaded image as a start frame or reference for generation.
- Drag uploaded video or audio from the canvas to the timeline when you are ready to cut. Its position on the canvas does not affect where it lands in the sequence; place it at the timeline playhead or between existing shots.

If an upload fails, confirm the file opens locally, then retry it by itself. This separates a damaged file from a larger batch.`,
  },
  {
    slug: 'canvas/stacks',
    title: 'Stacks',
    section: 'canvas',
    order: 9,
    description: 'Keep related takes together and compare them in place.',
    overview: `Stacks group related images or shots without flattening them into one asset. Use a stack for alternate takes, model comparisons, or variations of the same composition.

Create and review a stack

- Select two or more compatible images or shots.
- Choose Group into stack from the selection toolbar.
- Expand the stack to see every take, or collapse it to keep the canvas readable.
- Hold ||Alt|| and press an arrow key to cycle through the takes.

Choose Unstack from the context menu when the takes no longer belong together. Unstacking keeps the individual assets on the canvas.`,
  },
  {
    slug: 'canvas/clipboard',
    title: 'Copy, paste, duplicate',
    section: 'canvas',
    order: 7,
    description: 'Reuse canvas work while keeping the original arrangement intact.',
    overview: `Use copy and paste when you want to reuse selected work after doing something else on the canvas. Press ||⌘C|| to copy, then ||⌘V|| to paste. The new objects appear near the originals with their settings intact.

Use ||⌘D|| when you need an immediate duplicate and do not need the clipboard. This is useful for creating a nearby variation before changing a prompt, reference, or label.

Both workflows support multi-selection. If you copy a group of shots and references, Martini preserves the spacing between them when you paste or duplicate the group.`,
    featureIds: ['clipboard-copy', 'clipboard-paste', 'duplicate-node'],
  },
  {
    slug: 'canvas/undo-redo',
    title: 'Undo and redo',
    section: 'canvas',
    order: 8,
    description: 'Step backward or forward through your own recent canvas actions.',
    overview: `Press ||⌘Z|| to undo your last canvas action. You can undo creating, moving, renaming, or deleting objects. If you go back too far, press ||⌘ Shift Z|| to redo the action.

Undo follows each collaborator separately. It reverses the changes you made, not the most recent change anyone made to the project. If a collaborator moves a shot while you are working, your next undo leaves their move in place.

After an undo, check the selection and canvas before continuing. A new edit clears the redo path, just as it does in most editing applications.`,
    featureIds: ['undo', 'redo'],
  },
  {
    slug: 'canvas/rename',
    title: 'Renaming',
    section: 'canvas',
    order: 10,
    description: 'Give shots and references names that stay useful as the project grows.',
    overview: `Names make a crowded canvas easier to scan and help collaborators understand what they are looking at. Prefer a short, specific name such as “Kitchen wide — rain” over a generic label such as “Shot 4.”

Select an object and press ||Enter|| to edit its name on the canvas. Type the new name, then press ||Enter|| again to save it. Press ||Escape|| if you want to leave the original name unchanged.

The name appears below the object. Renaming changes the label, not the media or generation settings.`,
    featureIds: ['rename-inline'],
  },
  {
    slug: 'canvas/delete',
    title: 'Deleting',
    section: 'canvas',
    order: 11,
    description: 'Clear unwanted work from the canvas without deleting uploaded media from storage.',
    overview: `Select the work you no longer need, then press ||Delete|| or ||Backspace||. The command applies to every selected object, so check the selection borders before removing a group.

Deleting removes the objects from this canvas. It does not delete the underlying uploaded files from Martini's storage, and it does not affect another project that uses the same media.

If you remove something by mistake, press ||⌘Z|| before continuing. The objects return with their canvas positions and settings.`,
    featureIds: ['delete-selection'],
  },
  {
    slug: 'canvas/playback',
    title: 'Playback',
    section: 'canvas',
    order: 12,
    description: 'Review a take on the canvas before you add it to the timeline.',
    overview: `Select a shot and press ||Space|| to play or pause it in place. This is the quickest way to check motion, framing, and unwanted artifacts while comparing takes on the canvas.

Use the familiar ||J||, ||K||, and ||L|| controls for closer review: ||J|| plays backward, ||K|| stops, and ||L|| plays forward. Press ||L|| again to increase the forward speed. These controls follow the convention used by Premiere Pro, Avid, and DaVinci Resolve.

When the timeline is open, the program monitor shows the current timeline frame at a larger size. Canvas playback reviews the selected source; timeline playback reviews the assembled sequence.`,
    featureIds: ['playback-toggle', 'playback-forward', 'playback-backward', 'playback-stop'],
  },

  // ---------------------------------------------------------------------------
  // Generation
  // ---------------------------------------------------------------------------
  {
    slug: 'generation/overview',
    title: 'How generation works',
    navTitle: 'Overview',
    section: 'generation',
    order: 0,
    description: 'How AI generation works in Martini.',
    overview: `Martini connects to multiple AI models: Veo, Kling, Sora, and others. You do not need separate accounts or API keys. Pick a model, write a prompt, set the parameters, and generate. Every result lands on the canvas so you can iterate, rate, or cut it into the timeline.

Generation is the core loop. Place a shot, describe what you want, choose a model, and generate. If the result is not right, adjust and regenerate. The canvas gives you space to compare takes side by side.`,
  },
  {
    slug: 'generation/images',
    title: 'Images',
    section: 'generation',
    order: 1,
    description: 'Develop still frames for references, compositions, and image-to-video shots.',
    overview: `A still image can establish the composition before you spend time generating motion. Start from a text prompt, or upload photography, storyboards, and concept art when you already have a visual direction.

Generate an image

- Press ||I|| and click the canvas to place an image.
- Describe the subject, composition, lens or framing, lighting, and visual treatment.
- Choose an image model and add any references it supports.
- Review the olive estimate, then select Generate.

Keep the useful result beside the prompt that produced it. You can use that image as a start frame or reference for a video shot, then generate alternate frames without losing the earlier direction.

For a character or prop you expect to reuse, save its clearest stills to a Library collection and enable it in the project. Referencing the collection with @ in the prompt gives supported models more consistent visual information across shots, though you should still review each take for continuity.`,
  },
  {
    slug: 'generation/video',
    title: 'Video',
    section: 'generation',
    order: 2,
    description: 'Turn a prompt or reference frame into a moving shot.',
    overview: `A video generation produces a new take from your written direction and any references the selected model accepts. You can begin with text alone or use an image to hold the opening composition more closely.

Generate a take

- Press ||V|| and click the canvas to place a video shot.
- Write the action, camera movement, framing, and lighting you want to see.
- Choose a model, then add a start frame, end frame, or reference image if that model supports it.
- Set the available duration, resolution, and aspect ratio.
- Check the olive estimate and select Generate.

The result appears beside the source shot. Play it on the canvas before changing the direction. If one part is wrong, adjust that instruction or reference and generate another take so you can compare the effect.

Available controls vary by model. Martini only shows compatible parameters after you choose one, so changing models may add, remove, or reset an option.`,
  },
  {
    slug: 'generation/models',
    title: 'Models',
    section: 'generation',
    order: 3,
    description: 'Choose a model by the inputs, controls, speed, and cost your shot needs.',
    overview: `There is no single best model for every shot. Start with the constraint that matters most: a required start frame, a specific duration, native audio, faster iteration, or tighter control over motion. Then compare the compatible models in the picker.

Each model lists the media it can generate and the controls it accepts. Some work from text alone. Others accept reference images, start and end frames, or source video. Resolution, aspect ratio, duration, generation time, and olive cost also vary.

Use one model for the first useful take, then try another only when it offers a meaningful advantage for the shot. Changing several variables at once makes the comparison harder to judge.

You can hide models you do not use in Preferences. Hiding a model keeps the picker focused and does not affect earlier generations made with it.`,
  },
  {
    slug: 'generation/prompting',
    title: 'Prompting',
    section: 'generation',
    order: 4,
    description: 'Give the model concrete direction for subject, action, camera, and light.',
    overview: `Treat a prompt as a compact note to your cinematographer, performer, and effects team. Describe what should happen on screen, not how impressed the viewer should feel.

Build the prompt from observable choices:

- Subject: who or what the shot follows.
- Action: the physical change during the take.
- Framing: the shot size, angle, lens character, or composition.
- Camera: whether it is locked, handheld, tracking, panning, or moving another way.
- Light and environment: the visible source, time, weather, or atmosphere.

For example, “Handheld medium follow shot. A woman crosses the kitchen and pauses at the rain-streaked window. Soft overcast daylight, shallow focus” gives the model decisions it can render. “A beautiful cinematic shot of a person” does not.

Keep the first attempt focused. If the take misses, change one meaningful instruction at a time and compare the result with the previous take. Add a reference image when words alone do not hold the composition or identity closely enough.`,
  },
  {
    slug: 'generation/audio',
    title: 'Audio',
    section: 'generation',
    order: 5,
    description: 'Generate score, dialogue, ambience, and effects that are ready to edit.',
    overview: `Start by deciding what role the sound plays in the cut. A score prompt describes musical development; a dialogue prompt provides exact words and a performance; an effects prompt describes a physical source in a space. Choosing that role first makes the model list easier to navigate.

Audio generation can produce score, songs, foley, ambience, dialogue, voiceover, or a complete scene bed. Music models build cues and songs. Sound-effects models create discrete sounds and textures. Speech models perform written dialogue. All-in-one models combine several layers into one result.

Music

- MiniMax Music: generate a complete track or instrumental score from one prompt.
- Lyria 3.5: generate complete songs up to about three minutes with the strongest lyrics and vocals in the Lyria family, in eight languages.
- Lyria 3: generate 30-second jingles, vocal sketches, and quick music ideas.
- Lyria 3 Pro: generate polished songs up to about three minutes, including cinematic instrumentals and vocals.
- ElevenLabs Music v2.5: control the duration and build a song section by section.
- Stable Audio 2.5: generate short music, loops, ambience, foley, and sound-design textures.

Write music prompts like cue sheets. Name the tempo, meter, instrumentation, performance, and edit points: “0–8s sparse low strings; 8–20s brushed drums enter; 20–32s muted brass swell; no vocals.” For score, describe its job under picture: “Hold tension under dialogue; no melody until the reveal.”

Sound Effects

- ElevenLabs SFX v2: generate short foley, ambience, impacts, and atmospheric sounds.
- Beatoven SFX: generate longer stereo effects for creatures, vehicles, impacts, sci-fi, and abstract textures.
- CassetteAI SFX: generate fast placeholders and draft variations.
- Stable Audio 2.5: build longer ambience, texture beds, and music-adjacent sound design.

Describe a sound as a source in a scene. Name its distance, space, surface, and timing: “Single leather bootstep on wet concrete, close mic, parking-garage reverb, no music or voice.” Put multiple events in the order they should happen.

Dialogue

- ElevenLabs TTS v4: generate the most expressive ElevenLabs voiceover and character dialogue from exact wording. Direct delivery inline with audio tags like [whispers] or [laughs], and set Language for non-English lines (80+ languages).
- ElevenLabs TTS v4 Turbo: generate the same voices and audio tags as v4 faster, at half the price.
- ElevenLabs TTS v3: generate expressive voiceover, ADR, and character dialogue from exact wording.
- MiniMax Speech-02 HD: generate clean multilingual narration.
- Gemini 3.8 Flash TTS: generate expressive voiceover from exact wording, with the delivery (character, accent, pacing, emotion) directed separately in Style. Switch to Two Speakers to script a conversation between two named voices in one take, with optional direction per line.
- Gemini 3.8 Flash Lite TTS: generate high-volume voiceover at lower cost, with the same voices, two-speaker dialogue and style direction.
- Seed Audio 1.0: generate quick English or Chinese speech alongside soundtrack work.

Put the exact spoken words first, then add a short performance direction such as “calm documentary narrator, close mic, slower pace” or “teen character whispering, nervous, one breath before the last line.” Generate each speaker separately when you need editorial control. With Gemini TTS, put that direction in Style instead of the prompt so it isn't spoken, and keep vocal cues like <laugh> or <sigh> in the prompt.

All-in-one and native audio

- Seed Audio 1.0: compose dialogue, sound effects, and music into one scene bed.
- Native-audio video models: generate sound that follows the action in the shot.

For Seed Audio, describe the whole soundtrack in sequence: dialogue first, a door latch at two seconds, then a low synth pad underneath. Use native audio when the sound needs to follow visible action in the generated shot.

Each audio result lands on the canvas. Play it before changing the prompt, rename useful takes, then drag the select to an audio track in the timeline. Keep score, dialogue, and ambience on separate tracks so you can trim, balance, and bridge them across cuts. Generate extra handles when timing matters. Use Stable Audio 2.5 Inpaint when you need to repair one section without replacing the whole track.

Match the prompt to the duration. Ask for one clear event in short sound effects. Describe the full arc of a music cue. Keep dialogue to one sentence or beat when timing matters.

For a first pass, build a rough score bed and cut picture against it. Add effects for visible actions, then record or generate dialogue. Replace placeholders as the edit settles, and keep alternate takes nearby until the mix direction is clear.`,
  },
  {
    slug: 'generation/upscaling',
    title: 'Upscaling',
    section: 'generation',
    order: 6,
    description: 'Prepare a finished image or shot for a larger, cleaner, or HDR output.',
    overview: `Use Upscale & Enhance near the end of the workflow, after you have chosen the take and settled its framing. It can increase resolution, repair softness or noise, and create an HDR output for both images and video.

Select the image or shot, then choose Upscale & Enhance from the context menu or inspector. You can also press ||U||.

![](/docs-imgs/generationUpscale.mp4)

Choose the source

Source selects the version you want to process. Start with the original unless an earlier enhancement already contains a change you need. The info button shows its dimensions, file type, creation date, and asset ID.

Choose the operation

- Upscale increases the output resolution and can recover detail.
- Enhance reduces problems such as noise, blur, or compression without requiring a larger output.
- HDR creates a 10-bit or 16-bit high-dynamic-range file.

Choose the model and quality

The model determines the available operations and controls. General models suit most material; correction models target specific problems; the Starlight family favors higher quality. Images have separate fast and generative choices.

Leave Quality on automatic for a first pass. If the result looks over-sharpened or too smooth, switch to manual controls and adjust detail, noise, blur, grain, or compression deliberately. Compare against the source at the same viewing size.

Set the output

- Resolution sets the target size, from HD through the maximum supported by the selected model and codec. Martini shows the scale factor.
- Encoder sets the delivery format, including H.265, H.264, AV1, HDR, and ProRes options.
- Compression trades file size against retained detail. Lower compression creates a larger, cleaner file.
- Frame rate defaults to the source rate. Set a custom rate only when the delivery specification requires it.

If you expect to reuse the same combination, select the plus beside Preset and give it a clear name. Presets include the model, resolution, quality, encoder, and related settings, and are shared with your team.

Review the olive estimate before starting. Video cost depends on output duration; image cost depends on megapixels; the model and output size also affect the total.

The finished result appears as a new version in the same stack. Toggle between it and the source to check whether the added detail is real and whether faces, text, grain, and edges still look natural.

![](/docs-imgs/upscale.png)`,
  },
  {
    slug: 'generation/credits',
    title: 'Olives',
    section: 'generation',
    order: 7,
    description: 'Understand olive estimates, charges, balances, and failed-generation refunds.',
    overview: `Olives are Martini's unit for generation spend. Before you generate, Martini shows an estimate based on the model and its settings. Longer video, higher resolution, and some higher-cost models use more olives.

Open the account menu to check your current balance. The model picker and supported-models page help you compare costs before committing to a take.

Martini charges the generation when it begins. If the provider reports that the generation failed, Martini returns those olives automatically. A result that completes but does not match your creative direction still counts as a completed generation.

If the balance is too low for the selected settings, add olives or change to a less expensive model or output. A plan upgrade can also change the olives included with your account.`,
  },

  // ---------------------------------------------------------------------------
  // Library
  // ---------------------------------------------------------------------------
  {
    slug: 'library/overview',
    title: 'Asset library',
    navTitle: 'Overview',
    section: 'library',
    order: 0,
    description: 'Reusable characters, locations, and styles for any project.',
    overview: `The library holds the material you reuse across projects: characters, locations, costumes, styles, and reusable variables. Collections are the unit of reuse. Each one gathers the images, videos, and variables that define one subject, with a cover image, tags, and per-asset properties.

Projects add the collections they need from the Library tab. Type @ in a prompt to reference a collection, or a slice like @Ms. Rambutan/happy for one exact asset. When a take is worth keeping, right-click it and choose Add to Library, or drag it into the collection.`,
    searchText:
      'Asset library collections folders reusable characters props references upload import organize link project collection assets',
  },

  // ---------------------------------------------------------------------------
  // Sets
  // ---------------------------------------------------------------------------
  {
    slug: 'sets/overview',
    title: 'Sets overview',
    navTitle: 'Overview',
    section: 'sets',
    order: 0,
    description: '3D environments built from a single image.',
    overview: `Sets are 3D environments built from a single image. Upload or generate a reference photo, and Martini builds a navigable 3D space from it. Think of a set as a virtual location. You can move a camera through it, place characters, and generate shots from specific angles.

This is the closest equivalent to scouting and building a physical set. Same location, different camera positions, consistent geography across shots.`,
  },
  {
    slug: 'sets/creating',
    title: 'Create a set',
    section: 'sets',
    order: 1,
    description: 'Reconstruct a navigable environment from images or video.',
    overview: `A set begins with a clear view of the location. Martini uses that source to reconstruct a 3D environment you can explore and shoot from new camera positions.

Choose the source

- Use Image for a wide, sharp view that shows the shape of the space. Add supplementary images from overlapping angles when you have them.
- Use Video for a slow, steady sweep through the location. Coverage across 180–360 degrees works best; fast movement and zooms make the space harder to reconstruct.

Create the set

- Place a set on the canvas and add the main image or video in the Create set panel.
- If you use images, add up to seven supplementary views of the same space.
- Choose Fast for a quicker, coarse environment or Quality for more detail. Review the olive estimate, then select Create Set.

If the main image contains characters, Martini lets you choose which ones to carry into the set. Video sources do not extract characters automatically, but you can add them after the set is ready.

Set creation runs in the background. When the set finishes, open it from the canvas and check the reconstruction from several angles before framing a shot. If important geometry is unclear, try again with a wider main image or stronger overlapping coverage.`,
  },
  {
    slug: 'sets/characters',
    title: 'Characters',
    section: 'sets',
    order: 2,
    description: 'Place reusable subjects in a set and carry them across camera angles.',
    overview: `Characters give set-based shots a consistent subject reference. A character can be a person, creature, or object that you position inside the reconstructed environment before you capture a camera view.

When you create a set from an image, Martini detects the visible subjects and asks which ones to keep. A set currently supports two character slots, so select the two subjects you expect to use. A clear full-body view with distinct edges gives the placement step more useful information than a cropped or obscured figure.

Inside the set viewer, press ||Tab|| to switch between Camera and Character modes. In Character mode, select a character to move, rotate, or scale it. Use the numbered gizmo controls shown in the viewer when you need to change the kind of transform.

If a character slot is open, you can add another subject after the set is created. This is also the way to add characters to a set made from video, because video sources do not extract them automatically.

Character placement becomes part of the captured shot reference. It guides the generation model, but it does not lock every visual detail. Review identity, wardrobe, scale, and contact with the environment in each generated take.`,
  },
  {
    slug: 'sets/viewer',
    title: 'Set viewer',
    section: 'sets',
    order: 3,
    description: 'Explore a reconstructed environment, stage characters, and capture a shot.',
    overview: `The set viewer turns a set on the canvas into a navigable 3D location. Use it to scout the environment, place characters, and choose a camera view before you generate.

Move through the set

- Use ||W||, ||A||, ||S||, and ||D|| to move. Use ||Q||, ||E||, or ||Space|| to move vertically.
- Drag on empty space or scroll to look around. Hold ||Shift|| when you need to move faster.
- Press ||+|| or ||-|| to change the lens, and ||[|| or ||]|| to change the frame ratio.
- Press ||R|| to reset the view if you lose your position.

Press ||Tab|| to switch between Camera and Character modes when the set includes characters you can move. Camera mode changes the frame. Character mode lets you select and stage a subject without moving the camera.

When the composition is ready, select Capture or press ||Enter||. Martini places the captured shot beside the set on the canvas, preserving the camera and character setup used for that view. Continue from that shot to choose a generation model, refine the prompt, and generate takes.

Before you capture, check the frame edges and the apparent scale of each character. The set is a reference for generation rather than a final 3D render, so judge the resulting take on its own.`,
  },

  // ---------------------------------------------------------------------------
  // Timeline
  // ---------------------------------------------------------------------------
  {
    slug: 'timeline/overview',
    title: 'Timeline overview',
    navTitle: 'Overview',
    section: 'timeline',
    order: 0,
    description: 'Assemble shots into a sequence.',
    overview: `The timeline is where shots become a sequence. Drag shots from the canvas onto tracks, arrange them in order, trim, and preview the cut. It is not a replacement for Premiere or DaVinci, but it is enough to build a rough cut, check timing, and export a package for finishing.

Switch between the canvas and timeline with the mode switcher in the toolbar. The canvas is where you generate and organize. The timeline is where you cut.`,
  },
  {
    slug: 'timeline/clips',
    title: 'Clips and tracks',
    section: 'timeline',
    order: 1,
    description: 'Build an edit with clips, trim points, and video or audio tracks.',
    overview: `A shot becomes a clip when you add it to the timeline. The clip points back to the canvas media, so trimming or moving it changes the edit without changing the source take.

Build the sequence

- Drag a shot from the canvas onto a video track. Drag audio onto an audio track.
- Move a clip left or right to change its timing. With snapping enabled, clips align to the playhead, edit points, and nearby clips.
- Drag either edge to set the in or out point. Press ||B|| to split a selected clip at the playhead.
- Add video or audio tracks from the timeline settings menu when the sequence needs another layer.

Press ||S|| to toggle snapping. For frame-level adjustments, press ||,|| or ||.|| to nudge selected clips one frame; add ||Shift|| to move five frames.

Delete removes a clip and leaves the timing gap. Ripple Delete closes the gap by moving the later clips. Neither action deletes the source shot from the canvas.

If you need to find the source again, right-click the clip and choose Reveal in Canvas. A clip created only on the timeline offers Create on Canvas instead.`,
  },
  {
    slug: 'timeline/audio',
    title: 'Audio',
    section: 'timeline',
    order: 2,
    description: 'Arrange dialogue, music, effects, and generated sound against the picture.',
    overview: `Audio clips live on audio tracks beneath the picture. Their waveforms make dialogue, beats, and transients visible, so you can line up an edit without guessing from the file length.

Add and shape audio

- Drag an audio asset from the canvas to an audio track, then move it to the intended start time.
- Drag either edge to trim the clip. The source audio remains unchanged.
- Add another audio track when dialogue, music, or effects need separate control.
- Use the track controls to mute, solo, or lock a track while you review the mix.

Video with embedded audio creates linked video and audio clips in sequences that use separate track types. Moving or trimming one keeps the pair together. If you need to edit them independently, right-click either clip and choose Unlink Audio/Video. To link a video clip and an audio clip again, select one, hold ||Shift|| and click the other, then right-click either clip and choose Link Audio/Video. This works for a clip and its own audio while both still cover the same stretch of the timeline. If you moved or trimmed one after unlinking, undo that first.

Generated sound is treated like imported sound once it reaches the timeline. Play across each cut to check sync and continuity; a visible waveform can show timing, but it cannot tell you whether the transition sounds natural.`,
  },
  {
    slug: 'timeline/playback',
    title: 'Playback',
    section: 'timeline',
    order: 3,
    description: 'Review timing with transport, frame-step, and edit-point controls.',
    overview: `Timeline playback shows the active sequence in the program monitor. Use it to review shot order, trim points, audio sync, and the rhythm between edits before you export.

Control playback

- Press ||Space|| to play or pause.
- Press ||J|| to play backward, ||K|| to stop, and ||L|| to play forward. Press ||J|| or ||L|| again to increase shuttle speed.
- Press the left or right arrow to move one frame. Hold ||Shift|| with an arrow to jump one second.
- Press the up arrow to move to the previous edit point or the down arrow to move to the next one.
- Press ||Home|| to go to the start and ||End|| to go to the end.

The playhead marks the frame you are reviewing. Click the ruler to move it directly, or use frame stepping when you need to inspect the exact side of a cut.

If playback hesitates while media loads, pause briefly and try the section again. Before export, play through every edit and confirm that the intended video and audio tracks are visible and audible.`,
  },
  {
    slug: 'timeline/sequences',
    title: 'Sequences',
    section: 'timeline',
    order: 4,
    description: 'Keep scenes, alternate cuts, and delivery formats in separate edits.',
    overview: `A sequence is one self-contained edit with its own clips, tracks, playhead position, and output frame. Use separate sequences for scenes, alternate cuts, or deliverables that need different framing.

Create and switch sequences

- Open the sequence menu in the timeline toolbar and select New Sequence.
- Rename the sequence for the cut it contains, such as “Opening — selects” or “Trailer — vertical.”
- Use the same menu to switch sequences. Martini restores the timeline view for the sequence you return to.

Changing one sequence does not rearrange the others. This makes it safe to explore a new cut without dismantling the current one.

A sequence can also appear as a nested clip inside another sequence. Open the nested sequence to edit its source, or choose Break Link when you need an independent copy in the parent edit. Keep nested sequence framing consistent with the parent when you plan to export them together.

Before exporting, confirm the active sequence name and play it from beginning to end. Export uses the active sequence, not every sequence in the project.`,
  },

  // ---------------------------------------------------------------------------
  // Export
  // ---------------------------------------------------------------------------
  {
    slug: 'export/overview',
    title: 'Export from Martini',
    navTitle: 'Overview',
    section: 'export',
    order: 0,
    description: 'Get your work out of Martini.',
    overview: `Export gets your work out of Martini. Render an MP4 for a standalone video, or export an XML package for finishing in Premiere Pro or DaVinci Resolve. The XML package includes your media files and timeline structure, so you can open it in an editor and continue working.

Which format depends on where the project goes next. MP4 for reviews and deliverables. XML for post-production.`,
  },
  {
    slug: 'export/mp4',
    title: 'Export an MP4',
    section: 'export',
    order: 1,
    description: 'Render the active sequence as a review or delivery-ready video.',
    overview: `Export an MP4 when you need one playable video rather than an editable post-production package. Martini renders the active sequence with its current clip order, trim points, visible video tracks, and audio tracks that are not muted.

Prepare the sequence

- Play the active sequence from beginning to end and check every edit.
- Confirm that the sequence frame matches the intended delivery. If it contains nested sequences, make sure their frame formats are compatible.
- Hide any video track and mute any audio track you do not want in the file. Solo changes timeline playback, but it does not determine which audio tracks are exported.

Export the video

- Open Export in the timeline toolbar and choose Export MP4, or press ||Command M||.
- Choose Sequence to preserve the sequence frame, or select a YouTube, Short-form, Square, or Feed destination.
- Choose 1080p or 4K. Select Fit to show the full source frame with black space where needed, or Fill to use a centered crop that covers the output.
- Review the framing preview and output dimensions, then select Export MP4.

Martini renders an H.264 MP4 in the background and notifies you when the download is ready. A high-quality render commonly takes two to three times the sequence duration. The maximum sequence length for this export is 30 minutes.

Use the downloaded MP4 for review, presentation, or direct delivery. If the next step is color, sound, or VFX finishing in Premiere Pro or DaVinci Resolve, export XML instead.`,
  },
  {
    slug: 'export/nle',
    title: 'XML export',
    section: 'export',
    order: 2,
    description: 'Export an XMEML 5 package for Premiere Pro or DaVinci Resolve.',
    overview: `Export an XML package when you want to finish in Premiere Pro or DaVinci Resolve. Martini creates a ZIP containing a Premiere Pro XMEML 5 project file, a media folder, and an import README.

Import the package

- Unzip the package without moving the XML project file away from the media folder.
- In Premiere Pro, choose File > Import and select the XML file.
- In DaVinci Resolve, choose File > Import > Timeline and select the XML file.
- If your editor asks for missing files, relink it to the package's media folder.

Use XML for projects that need color grading, sound design, or VFX finishing. Review the imported sequence before finishing; editor versions can interpret transitions and unsupported effects differently.`,
  },

  // ---------------------------------------------------------------------------
  // Teams
  // ---------------------------------------------------------------------------
  {
    slug: 'teams/overview',
    title: 'Teams overview',
    navTitle: 'Overview',
    section: 'teams',
    order: 0,
    description: 'Shared workspaces for organizations.',
    overview: `Teams are shared workspaces for studios and agencies. Members share billing, model access settings, and project ownership. Team admins control who can join, which models are available, and how much each member can spend.

Create a team from your account settings. Invite members by email. Projects created within the team are visible to all members and draw from the team's olive balance.`,
  },
  {
    slug: 'teams/members',
    title: 'Members and roles',
    section: 'teams',
    order: 1,
    description: 'Invite collaborators and give each person the access their work requires.',
    overview: `Team membership gives a collaborator access to the shared workspace and its projects. Roles determine whether that person can only work in the workspace or can also manage it.

Choose a role

- Member: works in team projects and uses the models and billing available to the team.
- Admin: manages members, model access, settings, billing, and spend controls.
- Billing: an Enterprise role limited to billing, invoices, the Martini API, and provider connections. It does not grant ordinary project access.

Invite a member

- Open the team workspace, then open Members.
- Select Invite Member, enter the person's email address, and choose a role.
- Select Send Invite. The invitation remains pending until it is accepted or expires.

Admins can change another member's role, revoke a pending invitation, or remove a member. A member can also leave the team from the same page.

Removing someone ends their team access; it does not delete the team-owned projects or media they worked on. Before changing the role of an admin or removing them, make sure another admin can manage the workspace.`,
  },

  {
    slug: 'teams/billing',
    title: 'Team billing',
    section: 'teams',
    order: 2,
    description: 'Manage a shared plan, olive balance, usage, and monthly controls.',
    overview: `A team has its own plan and olive balance. A project labeled Team billing spends from that balance. A project labeled Pay per user spends from the personal account of the person generating.

Open Billing in the team workspace to review the current plan and balance. From there, an authorized billing user can buy olives, configure auto top-up, review recent spend, and open invoices. Admins can also manage the team plan and billing portal.

Understand usage

- Usage analytics breaks spend down by project, member, and model.
- Invoices record charges such as subscriptions and olive purchases.
- Olive activity records balance changes, including generation charges, top-ups, and eligible refunds.

Admins can set a monthly olive cap and an alert threshold for each member in Usage analytics. Caps reset on the first day of each month in UTC. When a member reaches the cap, new generations charged to the team are blocked until the cap resets or an admin changes it.

Personal and team balances stay separate. Before generating, check the project's billing label in the team workspace so you know which balance it will use.`,
  },
  {
    slug: 'teams/models',
    title: 'Manage team models',
    section: 'teams',
    order: 3,
    description: 'Choose which generation models team members can use.',
    overview: `Model Access is the team's shared list of approved generation models for projects labeled Team billing. Use it to keep the model picker aligned with the tools, costs, and review standards your team has chosen.

Change model access

- Open the team workspace, then open Settings and find Model Access.
- Toggle individual models, or enable or disable a whole capability group.
- Expand a model to enable or disable individual resolutions. Keep at least one resolution enabled unless the whole model is disabled.
- Select Save Changes. The new list applies to everyone using the team workspace.

A model or resolution disabled by an admin is unavailable in team-billed projects, even if a member has chosen to show the model in personal settings. Disabled resolutions remain visible in the generation dropdown with a team restriction note. Members can still hide additional models for their own account without changing access for anyone else. In projects labeled Pay per user, model and resolution access follows the personal account of the person generating.

Changing access does not remove media that was already generated. If an unfinished shot points to a model that is no longer available, choose an enabled model before generating again.

If the model you need is not listed, use Request Model. A request records the need for review; it does not add the model to the team immediately.`,
  },

  // ---------------------------------------------------------------------------
  // Collaboration
  // ---------------------------------------------------------------------------
  {
    slug: 'collaboration/sharing',
    title: 'Share a project',
    section: 'collaboration',
    order: 0,
    description: 'Invite editors and viewers to a live project.',
    overview: `Open Share from the project toolbar. The project owner can copy an invite link and choose who may open it.

Choose access

- Anyone with link (edit): people with the link can join and change the project.
- Anyone with link (view): people with the link can open the project without editing it.
- Private: only you, or members of the team that owns the project, can open it. Private access depends on your plan.

People with access appear as Owner, Editor, or Viewer. Team members can also receive access through the team rather than an individual project invitation.

Work together

- Canvas and timeline edits sync in real time.
- Collaborator cursors show where other people are working.
- Use comments to leave context beside the work.
- Undo reverses your own actions, not a collaborator's actions.

Treat edit links like workspace invitations. Switch to view-only or private access when open editing is no longer needed.`,
  },

  // ---------------------------------------------------------------------------
  // Account
  // ---------------------------------------------------------------------------
  {
    slug: 'account/profile',
    title: 'Account and profile',
    section: 'account',
    order: 0,
    description: 'Manage your collaborator identity, sign-in options, and notifications.',
    overview: `Your profile is how other people recognize you in Martini. The display name and color appear on collaborator cursors and other shared-project activity, while your email connects sign-in and team invitations to your account.

Open Settings > Account to update your display name or choose a different color. Display-name changes save after you stop typing; color changes save when you select one.

The Account page also shows your sign-in methods. If you already use email and password, enter the current password before setting a new one. If you signed up with Google, you can set a password to add email-and-password sign-in.

Use Comment emails to choose whether Martini emails you when someone mentions you or replies to one of your threads. This setting changes email notifications, not the comments stored in a project.

Your Stats is a read-only summary of completed generations by capability. It appears after the account has completed at least one generation.`,
  },
  {
    slug: 'account/preferences',
    title: 'Model preferences',
    section: 'account',
    order: 1,
    description: 'Keep the model picker focused on the tools you use.',
    overview: `Personal model preferences control which available models appear on your canvas. Hiding a model shortens the picker without changing team access or deleting any previous generations.

Choose visible models

- Open Settings > Models.
- Toggle a model to show or hide it, or use the group controls to change a whole capability at once.
- Return to a project and open the model picker to review the shorter list.

These choices follow your account across personal projects and team projects labeled Pay per user. In a project labeled Team billing, the team's Model Access settings take precedence: you cannot show a model that the team has disabled, but you can hide more models for yourself.

Use Browse models to see additional catalog options. If a needed model is not available, select Request Model or submit a custom request. A request does not make the model available immediately.`,
  },
  {
    slug: 'account/billing',
    title: 'Billing',
    section: 'account',
    order: 2,
    description: 'Review your personal plan, olive balance, spend, and invoices.',
    overview: `Personal billing covers your own plan and the projects in your personal workspace. It is separate from every team billing account you belong to.

Open Settings > Billing to see your current plan and personal olive balance. You can open the olive store for a one-time top-up, configure auto top-up, or use the billing portal to manage the saved payment method and subscription.

Review the account

- Spend summary shows how many personal olives were used.
- Recent activity lists balance changes such as plan grants, top-ups, generation charges, and eligible refunds.
- Invoices lists subscription and purchase records and provides downloadable invoice files.
- Spend by project helps you find which personal projects account for the usage.

Before buying olives, check the project's billing label. A personal project spends personal olives. In a team workspace, a project labeled Team billing spends team olives, while a project labeled Pay per user spends the generator's personal olives. Adding olives to one balance does not increase the other.

If you change or cancel a plan, review the effective date in Billing before relying on the new limits. Remaining personal olives stay with the account.`,
  },

  // ---------------------------------------------------------------------------
  // Developers
  // ---------------------------------------------------------------------------
  {
    slug: 'api',
    title: 'Generation API',
    section: 'developers',
    order: 0,
    description: 'Generate images and video from server-side JavaScript or TypeScript.',
    // The page renders ApiGenerationDocs; this overview feeds Learn search and
    // /llms-full.txt, where a null body left the article as one stub line.
    overview:
      'The Generation API runs Martini generation from server-side JavaScript or TypeScript. Requests go through an asynchronous queue: submit a generation, then subscribe to updates or poll its status, and fetch the result when it completes. Results can land on a project canvas, so API-generated shots sit alongside work done in the app.\n\nAccess uses a Martini API key tied to your account; generation spends olives from the same balance as the app. The full reference with request and response shapes lives at https://www.martini.film/docs/api',
    searchText:
      'Generation API JavaScript TypeScript SDK client API key asynchronous queue submit subscribe status result image video canvas placement olives server integration',
  },
  {
    slug: 'workflows',
    title: 'Workflows API',
    section: 'developers',
    order: 1,
    description: 'Start runs of saved Martini workflows over HTTPS, poll their status, and collect the output files.',
    // The page renders WorkflowsApiDocs; same reason as the api overview above.
    overview:
      'The Workflows API executes reusable processes you have saved in Martini. Start a run with your own material, poll its status, and collect the output files when it settles. It does not author or edit workflows; do that on a canvas in Martini or through the MCP connector. Runs started over the API auto-approve, so nobody has to click anything in the app once a run is submitted.\n\nThe same Martini API key serves the Generation API, and every workflow route lives under https://api.martini.film/v1. A browser playground at https://www.martini.film/docs/workflows/playground issues the four calls with your own key and shows the curl for each. The full reference with the run object, budget rule, and error codes lives at https://www.martini.film/docs/workflows',
    searchText:
      'Workflows API HTTPS run start poll status results output files API key Bearer budget playground curl TypeScript Python client automation',
  },
  {
    slug: 'cli',
    title: 'Martini CLI',
    section: 'developers',
    order: 2,
    description:
      'Every Martini MCP tool as a command, for coding agents with a shell and for scripts: sign in, upload and download material, generate and wait, edit project memory files.',
    // The page renders CliDocs; same reason as the api overview above.
    overview:
      'The martini command reaches the same tools as the MCP connector, with the same names and the same arguments. What differs is the transport: a tool call is one HTTPS request with a key, so a shell script, a coding agent, or a workflow run can use Martini without holding an MCP session open, and can pipe results, submit JSON it computed, and block until work settles.\n\nA shell means the CLI: an agent that has one uses martini for every Martini task, even with the MCP connector installed; the connector is for hosts without a shell (Claude Desktop, ChatGPT, claude.ai). Install it with npm install --global @martini-film/cli (or run it with npx @martini-film/cli), sign in with martini login, and read the full command, output, and exit-code reference at https://www.martini.film/docs/cli',
    searchText:
      'Martini CLI command line shell terminal npx @martini-film/cli martini login upload download wait files probe whoami version exit codes coding agent scripts',
  },
  {
    slug: 'mcp',
    // The indexed /docs/mcp page keeps the title it ranked under; the sidebar
    // shortens it.
    title: 'MCP Connector',
    navTitle: 'MCP setup',
    section: 'developers',
    order: 3,
    description:
      'Connect Martini to Claude, Gemini CLI, and other Model Context Protocol clients. Use ChatGPT when Martini is available to your workspace. Browse projects, manage assets, and generate videos and images from your AI assistant.',
    // The page renders McpConnectorDocs; same reason as the api overview above.
    overview:
      'Martini exposes its production surface over the Model Context Protocol, so an AI agent can direct a shoot without a browser: read project and board context, create and edit shots on the canvas, generate with any supported model, work the timeline, and export XML for Premiere or Resolve.\n\nThe endpoint is https://www.martini.film/mcp (Streamable HTTP, OAuth 2.1 — sign in once in a browser and the token persists). A narrower profile at https://www.martini.film/mcp-core omits direct generation tools for clients that disallow AI media generation. Martini enforces account permissions, balance, spend limits, and content rules server-side; generation tools spend olives, so review model, prompt, and estimated cost before approving a call. Per-client setup guides (Claude, ChatGPT, Cursor, Gemini CLI, and more) live under https://www.martini.film/docs/mcp',
    searchText:
      'Model Context Protocol MCP assistant agent Claude ChatGPT Gemini Cursor project canvas tools OAuth read create edit generate setup',
  },
  ...MCP_CLIENTS.map((client, index) => ({
    slug: `mcp/${client.slug}`,
    title: client.pageTitle,
    section: 'developers',
    order: index + 4,
    description: client.description,
    overview: null,
    searchText: `${client.sidebarLabel} ${client.description} MCP OAuth agent setup Martini projects`,
  })),
]

export const DOCS_PAGE_MAP = new Map(DOCS_PAGES.map(p => [p.slug, p]))

const DOCS_TRANSLATIONS: Partial<Record<DocsLocale, Record<string, DocsPageTranslation>>> = {
  ja: DOCS_PAGE_TRANSLATIONS_JA,
  ar: DOCS_PAGE_TRANSLATIONS_AR,
  es: DOCS_PAGE_TRANSLATIONS_ES,
  ko: DOCS_PAGE_TRANSLATIONS_KO,
}

function getDocsTranslation(slug: string, locale: DocsLocale): DocsPageTranslation | undefined {
  if (locale === 'en') return undefined
  return DOCS_TRANSLATIONS[locale]?.[slug]
}

function applyDocsTranslation(
  page: DocsPage,
  translation: DocsPageTranslation | undefined,
  locale: DocsLocale,
): DocsPage {
  // `navTitle` is an English-only nav shorthand. Dropping it for every other
  // locale keeps a localized sidebar on the page's own title — translated where
  // a translation exists, English where it doesn't — instead of mixing a bare
  // English "Overview" into localized labels. Keying off the locale rather than
  // the translation also covers pages no locale has translated yet.
  const base = locale === 'en' ? page : { ...page, navTitle: undefined }
  if (!translation) return base

  return {
    ...base,
    ...(translation.title !== undefined ? { title: translation.title } : {}),
    ...(translation.description !== undefined ? { description: translation.description } : {}),
    ...(translation.overview !== undefined ? { overview: translation.overview } : {}),
    source_en_hash: translation.source_en_hash,
  }
}

export function getDocsPages(locale: DocsLocale = 'en'): DocsPage[] {
  return DOCS_PAGES.map(page => applyDocsTranslation(page, getDocsTranslation(page.slug, locale), locale))
}

export function getDocsPage(slug: string, locale: DocsLocale = 'en'): DocsPage | undefined {
  const page = DOCS_PAGE_MAP.get(slug)
  if (!page) return undefined
  return applyDocsTranslation(page, getDocsTranslation(slug, locale), locale)
}

export function getDocsPagesForSection(sectionId: string, locale: DocsLocale = 'en'): DocsPage[] {
  return DOCS_PAGES.filter(p => p.section === sectionId)
    .sort((a, b) => a.order - b.order)
    .map(page => applyDocsTranslation(page, getDocsTranslation(page.slug, locale), locale))
}

export function getDocsTranslationStatuses(locale: Exclude<DocsLocale, 'en'> = 'ja'): DocsTranslationStatus[] {
  const translations = DOCS_TRANSLATIONS[locale] ?? {}

  return DOCS_PAGES.map(page => {
    const translation = translations[page.slug]
    const expected = getDocsSourceHash(page)

    return {
      slug: page.slug,
      locale,
      title: page.title,
      status: !translation ? 'missing' : translation.source_en_hash === expected ? 'current' : 'stale',
      expected_source_en_hash: expected,
      source_en_hash: translation?.source_en_hash,
    }
  })
}

export function getStaleDocsTranslations(locale: Exclude<DocsLocale, 'en'> = 'ja'): DocsTranslationStatus[] {
  return getDocsTranslationStatuses(locale).filter(item => item.status !== 'current')
}
